import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  DescriptionItem,
  DescriptionList,
} from '@billda/design-system';
import { FeatureSwitchGate } from '../../../../../shared/components/gates/FeatureSwitchGate';
import { Features } from '@billda/features-ui';
import { PermissionGate } from '../../../../../shared/components/gates/PermissionGate';
import { Permissions, usePermission } from '@billda/permissions-ui';
import { UserStatusBadge } from '../atoms/UserStatusBadge';
import { UserStatus } from '../../../api/userManagementApi';
import type { UserDetailDto } from '../../../api/userManagementApi';
import { getUserDisplayName } from '../../../utils/userDisplayUtils';
import { canManageUserOffboarding } from '../../../utils/roleChangePolicy';
import { routeBuilders } from '../../../../../routes';

interface UserOverviewCardProps {
  user: UserDetailDto;
  formatDate: (dateString: string) => string;
  isCurrentUser: boolean;
  showIdentity?: boolean;
}

export function UserOverviewCard({
  user,
  formatDate,
  isCurrentUser,
  showIdentity = true,
}: UserOverviewCardProps) {
  const navigate = useNavigate();
  const canManageAdmins = usePermission(Permissions.Users.ManageAdmins);
  const canOffboard = canManageUserOffboarding({
    roles: user.roles,
    isCurrentUser,
    canManageAdmins,
  });
  const getPrimaryPhone = () => {
    if (!user.phoneNumbers || user.phoneNumbers.length === 0) {
      return null;
    }
    const primary = user.phoneNumbers.find(p => p.isPrimary);
    const phone = primary || user.phoneNumbers[0];
    return `${phone.phoneNumber}${primary ? ' (Primary)' : ''}`;
  };

  const primaryPhone = getPrimaryPhone();

  return (
    <Card data-testid="user-overview-card">
      {showIdentity && (
        <CardHeader>
          <h2 className="type-h6 min-w-0 truncate">
            {getUserDisplayName(user)}
          </h2>
          {user.status && <UserStatusBadge status={user.status} />}
        </CardHeader>
      )}
      <CardContent className="pt-4">
        <div className="flex flex-col gap-4">
          <DescriptionList className="type-body-md">
            {user.roles && user.roles.length > 0 && (
              <DescriptionItem
                label="Roles"
                value={user.roles.map(role => role.roleName).join(', ')}
              />
            )}
            {user.email && <DescriptionItem label="Email" value={user.email} />}
            {primaryPhone && (
              <DescriptionItem label="Phone" value={primaryPhone} />
            )}
            {user.profile?.utrNumber && (
              <DescriptionItem label="UTR" value={user.profile.utrNumber} />
            )}
            {user.joinedAt && (
              <DescriptionItem
                label="Joined"
                value={formatDate(user.joinedAt)}
              />
            )}
          </DescriptionList>
          <FeatureSwitchGate
            feature={Features.Offboarding.IsEnabled}
            fallback={null}
          >
            <PermissionGate
              permission={Permissions.Users.Manage}
              fallback={null}
            >
              {canOffboard &&
                (user.status === UserStatus.Active ||
                  user.status === UserStatus.Offboarding) && (
                  <Button
                    variant={
                      user.status === UserStatus.Active
                        ? 'outlineDanger'
                        : 'outlineWarning'
                    }
                    fullWidth
                    onClick={() =>
                      navigate(routeBuilders.users.offboarding(user.id))
                    }
                    data-testid="offboard-manage-button"
                  >
                    {user.status === UserStatus.Active
                      ? 'Offboard'
                      : 'Manage Offboarding'}
                  </Button>
                )}
            </PermissionGate>
          </FeatureSwitchGate>
        </div>
      </CardContent>
    </Card>
  );
}
